// Shared by the browser and publisher. Art metadata is independent of rule/distribution bytes.
import { Checker, type Issue } from '../presentation/validate.ts';
import { hashManifestBytes } from './hash.ts';

export interface ArtRef { id: string; sha256: string }
export interface ArtImage { sha256: string; type: 'image/png' | 'image/jpeg' | 'image/webp'; width: number; height: number; bytes: number }
export interface ArtAsset { id: string; label: string; license: string; attribution?: string; fallback: string; images: ArtImage[] }
export interface ArtRelease { formatVersion: 'art-release-1'; id: string; assets: ArtAsset[] }
export const ART_HASH = /^[a-f0-9]{64}$/;
export const ART_ID = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/;
export const MAX_ART_JSON = 2 * 1024 * 1024;
export const MAX_ART_IMAGE = 8 * 1024 * 1024;

export function validateArtRelease(value: unknown): Issue[] {
  const c = new Checker('art-release.json');
  if (!c.object(value, '$', ['formatVersion', 'id', 'assets'])) return c.issues;
  const r = value as unknown as ArtRelease;
  c.oneOf(r.formatVersion, '$.formatVersion', ['art-release-1']); c.string(r.id, '$.id', 64, ART_ID);
  const ids = new Set<string>();
  if (c.array(r.assets, '$.assets', 0, 2048)) r.assets.forEach((a, i) => {
    const p = `$.assets[${i}]`;
    if (!c.object(a, p, ['id', 'label', 'license', 'fallback', 'images'], ['attribution'])) return;
    if (c.string(a.id, p + '.id', 128, /^[A-Za-z0-9][A-Za-z0-9:_.\/-]{0,127}$/)) {
      if (ids.has(a.id)) c.error(p, 'Duplicate artwork ID.'); ids.add(a.id);
    }
    c.string(a.label, p + '.label', 256); c.string(a.license, p + '.license', 128);
    c.string(a.fallback, p + '.fallback', 64);
    if (a.attribution !== undefined) c.string(a.attribution, p + '.attribution', 512);
    if (c.array(a.images, p + '.images', 1, 8)) {
      let previous = 0; let ratio: number | null = null;
      a.images.forEach((img, j) => {
        const q = `${p}.images[${j}]`;
        if (!c.object(img, q, ['sha256', 'type', 'width', 'height', 'bytes'])) return;
        c.string(img.sha256, q + '.sha256', 64, ART_HASH);
        c.oneOf(img.type, q + '.type', ['image/png', 'image/jpeg', 'image/webp']);
        c.number(img.width, q + '.width', 1, 4096, true); c.number(img.height, q + '.height', 1, 4096, true);
        c.number(img.bytes, q + '.bytes', 1, MAX_ART_IMAGE, true);
        if (img.width <= previous) c.error(q, 'Images must have distinct, increasing widths.');
        if (ratio !== null && Math.abs(img.width / img.height - ratio) > 0.02) c.error(q, 'Variants must preserve aspect ratio.');
        previous = img.width; ratio = img.width / img.height;
      });
    }
  });
  return c.issues;
}

/** Smallest sufficient image; bounded DPR avoids downloading huge images for small pieces. */
export function chooseArtImage(images: ArtImage[], cssWidth: number, dpr = 1): ArtImage | undefined {
  const wanted = Math.max(1, cssWidth) * Math.min(2, Math.max(1, dpr));
  return images.find(i => i.width >= wanted) ?? images.at(-1);
}

export async function fetchArtRelease(ref: ArtRef): Promise<ArtRelease> {
  if (!ART_ID.test(ref.id) || !ART_HASH.test(ref.sha256)) throw new Error('Invalid art reference.');
  const response = await fetch(`/art/${ref.sha256}/manifest.json`, { signal: AbortSignal.timeout(10000) });
  if (!response.ok) throw new Error('Artwork is unavailable.');
  // Bound the streamed response too: Content-Length alone is not a trustworthy bound.
  const reader = response.body?.getReader(); if (!reader) throw new Error('Empty artwork manifest.');
  const chunks: Uint8Array[] = []; let size = 0;
  try { for (;;) { const { value, done } = await reader.read(); if (done) break; size += value.length; if (size > MAX_ART_JSON) throw new Error('Artwork manifest exceeds its budget.'); chunks.push(value); } }
  finally { await reader.cancel(); }
  const bytes = new Uint8Array(size); let at = 0; for (const chunk of chunks) { bytes.set(chunk, at); at += chunk.length; }
  const hash = globalThis.crypto?.subtle
    ? [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))].map(b => b.toString(16).padStart(2, '0')).join('')
    : hashManifestBytes(bytes);
  if (hash !== ref.sha256) throw new Error('Artwork manifest hash mismatch.');
  const release: unknown = JSON.parse(new TextDecoder().decode(bytes));
  if (validateArtRelease(release).some(i => i.severity === 'error') || (release as ArtRelease).id !== ref.id) throw new Error('Invalid artwork manifest.');
  return release as ArtRelease;
}
